import React, { useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useMutation } from '@apollo/client';
import dayjs, { Dayjs } from 'dayjs';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';

import { useToast } from '../../../common/hooks/useToast';
import { describeError } from '../../../common/rateLimit';
import { OutcomeChip } from '../callLog/CallLogTable';
import { CRM_COMPLETE_FOLLOW_UP } from '../calls/calls.queries';
import { forgetCallData } from '../calls/LogCallForm';
import { contactPath, crmReturnState } from '../crmPaths';
import {
  describeWhenDue,
  formatFollowUpAt,
  FollowUpRow,
  NOT_CALLABLE,
  notCallable,
  personName,
  setByName,
} from './followUps';

const NONE = (
  <Box component="span" sx={{ color: '#cbd5e1' }}>
    —
  </Box>
);

const COLUMNS = ['Due', 'Person', 'Set by', 'The call', 'Notes', ''];

interface FollowUpsTableProps {
  rows: Array<FollowUpRow>;
  now: Dayjs;
  /** The admin asking, when the counts say who that is: their follow-ups read "You". */
  myAdminId: number | null;
  stale: boolean;
  onLog: (row: FollowUpRow) => void;
  /** After a follow-up is closed by hand. */
  onClosed: (row: FollowUpRow) => void;
}

/**
 * Open follow-ups, the earliest first: when, whom, who set it on which call,
 * and that call's notes. Each row offers the call back (which closes the
 * follow-up) and closing it by hand, which asks first: a closed follow-up
 * cannot be opened again.
 */
export const FollowUpsTable = ({ rows, now, myAdminId, stale, onLog, onClosed }: FollowUpsTableProps) => (
  <TableContainer
    sx={{
      opacity: stale ? 0.55 : 1,
      transition: 'opacity 120ms',
      '& .MuiTableCell-root': { borderColor: '#eef1f5', px: 1.25 },
      '& .MuiTableCell-head': { color: 'text.secondary', fontSize: '0.75rem', letterSpacing: '0.02em', py: 1.25, fontWeight: 600, whiteSpace: 'nowrap' },
      '& .MuiTableCell-body': { py: 1, fontSize: '0.8125rem', whiteSpace: 'nowrap', verticalAlign: 'top' },
      '& .MuiTableRow-hover:hover': { backgroundColor: '#f8fafc' },
    }}
  >
    <Table stickyHeader size="small" aria-label="Follow-ups" aria-busy={stale}>
      <TableHead>
        <TableRow>
          {COLUMNS.map((label, index) => (
            <TableCell key={index} sx={index === 0 ? { pl: 2 } : undefined}>
              {label}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {rows.map((row) => (
          <FollowUpLine key={row.id} row={row} now={now} myAdminId={myAdminId} onLog={onLog} onClosed={onClosed} />
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

interface FollowUpLineProps {
  row: FollowUpRow;
  now: Dayjs;
  myAdminId: number | null;
  onLog: (row: FollowUpRow) => void;
  onClosed: (row: FollowUpRow) => void;
}

const FollowUpLine = ({ row, now, myAdminId, onLog, onClosed }: FollowUpLineProps) => {
  const here = useLocation();
  const toast = useToast();
  const [confirming, setConfirming] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [complete, { loading: closing }] = useMutation(CRM_COMPLETE_FOLLOW_UP, { update: (cache) => forgetCallData(cache) });

  const name = personName(row.contact, row.contactId);
  const blocked = notCallable(row.contact);
  const followUpAt = row.followUpAt as string;
  const by = setByName(row, myAdminId);
  const merged = row.contact?.mergedIntoId ?? null;

  const close = async () => {
    try {
      await complete({ variables: { callId: row.id } });
      toast.success(`Follow-up with ${name} closed.`);
      onClosed(row);
    } catch (error) {
      toast.error(describeError(error, 'The follow-up could not be closed'));
      setConfirming(false);
    }
  };

  return (
    <TableRow hover data-follow-up={row.id}>
      <TableCell sx={{ pl: '16px !important', fontVariantNumeric: 'tabular-nums' }}>
        <Tooltip title={dayjs(followUpAt).format('dddd D MMMM YYYY, HH:mm')}>
          <Box component="span" sx={{ display: 'block', fontWeight: 600, color: row.followUpDue ? 'error.main' : 'text.primary' }}>
            {describeWhenDue(followUpAt, now)}
          </Box>
        </Tooltip>
        <Typography variant="caption" color="text.secondary">
          {formatFollowUpAt(followUpAt, now)}
        </Typography>
      </TableCell>

      <TableCell sx={{ whiteSpace: 'normal !important', minWidth: 160 }}>
        <Link
          component={RouterLink}
          to={contactPath(row.contactId)}
          state={crmReturnState(here)}
          underline="hover"
          sx={{ fontWeight: 600, color: blocked ? 'text.secondary' : 'text.primary', '&:hover': { color: 'primary.main' } }}
        >
          {name}
        </Link>
        {blocked && (
          <Tooltip describeChild title={NOT_CALLABLE[blocked].hint}>
            <Chip
              size="small"
              color={blocked === 'doNotCall' || blocked === 'doNotContact' ? 'error' : 'default'}
              variant={blocked === 'doNotContact' ? 'filled' : 'outlined'}
              label={NOT_CALLABLE[blocked].label}
              sx={{ ml: 1, height: 18, fontSize: '0.6875rem', fontWeight: 600 }}
            />
          </Tooltip>
        )}
        {merged != null ? (
          <Typography variant="caption" component="div">
            <Link component={RouterLink} to={contactPath(merged)} state={crmReturnState(here)} underline="hover">
              Now contact #{merged}
            </Link>
          </Typography>
        ) : (
          row.contact?.primaryPhone && (
            <Typography variant="caption" color="text.secondary" component="div" sx={{ textDecoration: blocked ? 'line-through' : undefined }}>
              {row.contact.primaryPhone}
            </Typography>
          )
        )}
      </TableCell>

      <TableCell>{by || NONE}</TableCell>

      <TableCell>
        <OutcomeChip outcome={row.outcome} />
        <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.25 }}>
          {dayjs(row.occurredAt).format(dayjs(row.occurredAt).isSame(now, 'year') ? 'D MMM, HH:mm' : 'D MMM YYYY')}
        </Typography>
      </TableCell>

      {/* The one column that gives way: two lines, the rest on a click. */}
      <TableCell sx={{ whiteSpace: 'normal !important', minWidth: 200, maxWidth: 420 }}>
        {row.notes ? (
          <Box
            component="button"
            type="button"
            aria-expanded={notesOpen}
            aria-label={notesOpen ? `Shorten the notes on ${name}` : `Show all the notes on ${name}`}
            onClick={() => setNotesOpen((open) => !open)}
            sx={{
              all: 'unset',
              cursor: 'pointer',
              display: '-webkit-box',
              WebkitLineClamp: notesOpen ? 'unset' : 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              whiteSpace: notesOpen ? 'pre-wrap' : 'normal',
              wordBreak: 'break-word',
              lineHeight: 1.4,
              '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main', borderRadius: 0.5 },
            }}
          >
            {row.notes}
          </Box>
        ) : (
          NONE
        )}
      </TableCell>

      <TableCell align="right" sx={{ pr: '12px !important' }}>
        {confirming ? (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }} role="group" aria-label={`Close the follow-up with ${name}?`}>
            <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', md: 'inline' } }}>
              Close without calling?
            </Typography>
            <Button size="small" onClick={() => setConfirming(false)} disabled={closing}>
              Keep it
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              onClick={close}
              disabled={closing}
              startIcon={closing ? <CircularProgress size={14} color="inherit" /> : undefined}
            >
              Close it
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'inline-flex', gap: 0.75 }}>
            {merged == null && (
              <Button
                size="small"
                variant={blocked ? 'text' : 'outlined'}
                startIcon={<PhoneOutlinedIcon sx={{ fontSize: '16px !important' }} />}
                onClick={() => onLog(row)}
                aria-label={`Log a call with ${name}`}
              >
                Log call
              </Button>
            )}
            <Tooltip describeChild title="They were reached another way, or it is no longer needed. Logging a call closes it by itself.">
              <Button size="small" color="inherit" onClick={() => setConfirming(true)} aria-label={`Close the follow-up with ${name}`}>
                Close…
              </Button>
            </Tooltip>
          </Box>
        )}
      </TableCell>
    </TableRow>
  );
};
